<template>
  <form @submit.prevent='onSubmit' :class='{ errors: $v.$error }' novalidate>
    <div v-show="currentStep == 1">
      <p>{{formData().description}}</p>

      <div class='form-group'>
        <input v-model.trim='email' type='email' placeholder='Your email address'>
        <div class='error' v-if='!$v.email.required'>Email is required</div>
        <div class='error' v-if='!$v.email.email'>Email is not the right format</div>
      </div>

      <div v-if="form_type === 'media'">
        <div class="row">
          <div class='form-group col m6'>
            <input type='text' v-model.trim='publication' placeholder='Publication or channel URL'>
            <div class='error' v-if='!$v.publication.required'>Publication or channel URL is required</div>
          </div>

          <div class='form-group col m6'>
            <input type='text' v-model.trim='deadline' placeholder='Deadline (if applicable)'>
          </div>
        </div>
      </div>

      <div class='form-group'>
        <textarea v-model.trim='message' placeholder="Detailed description of your issue"></textarea>
        <div class='error' v-if='!$v.message.required'>This field is required</div>
      </div>

      <div v-if="form_type === 'technical'">
        <div  class="row">
          <div class='form-group col m6'>
            <select v-model='cameraBrand'>
              <option disabled selected value="">Camera Brand</option>
              <option v-for="brand in cameraBrands" :key="brand">{{brand}}</option>
            </select>
            <div class='error' v-if='!$v.cameraBrand.required'>Camera Brand is required</div>
          </div>
          <div class='form-group col m6'>
            <select v-model='cameraModel'>
              <option disabled selected value="">Camera Model</option>
              <option disabled v-if="!cameraBrand">Please select your brand of camera</option>
              <option v-for="model in cameraModels[cameraBrand]" :key="model">{{model}}</option>
            </select>
            <div class='error' v-if='!$v.cameraModel.required'>Camera Model is required</div>
          </div>
        </div>

        <div  class="row">
          <div class='form-group col m6'>
            <select v-model='phoneOS'>
              <option disabled selected value="">Phone Operating System</option>
              <option value="iOS">iOS</option>
              <option value="Android">Android</option>
            </select>
            <div class='error' v-if='!$v.phoneOS.required'>Phone Operating System is required</div>
          </div>
        </div>

        <div class='form-group'>
          <input type="text" v-model.trim='arsenalVersion' placeholder="Arsenal App Version Number">
          <small>You can find this in the Arsenal mobile app at the bottom of the “Welcome” screen or by tapping the gear icon and scrolling to the bottom.</small>
        </div>
      </div>

      <div v-if="form_type === 'orders'">
        <div class='form-group'>
          <input type="text" v-model.trim='confirmationNumber' placeholder="Your order confirmation number">
          <div class='error' v-if='!$v.confirmationNumber.required'>Confirmation number is required</div>
        </div>

        <div class='form-group'>
          <input type="text" v-model.trim='trackingNumber' placeholder="Your shipment tracking number (if received)">
        </div>
      </div>

      <input v-model.trim='honeypot' placeholder='name' style='opacity: 0; position: absolute; width: 0px; height: 0px; z-index: -1'>

      <spinner-button :spin='submitting' class='btn-large'>Send Question</spinner-button>
    </div>

    <div v-show="currentStep == 2">
      <p>Thanks, someone from the Arsenal team will be in touch as soon as we can!</p>
      <p>In the meantime, you can also check out our <a href="/faq">FAQ page</a> for some of the most commonly asked questions.</p>
    </div>
  </form>
</template>

<script>
  import cameras from 'app/cameras.json'
import SpinnerButton from 'app/components/shared/SpinnerButton'
import { email, required, requiredIf } from 'vuelidate/lib/validators'

  let supported_cameras = []
  Object.keys(cameras).forEach(brand => {
    cameras[brand].forEach(camera => {
      if (camera.arsenal_support.length > 0) {
        supported_cameras.push(camera)
      }
    })
  })

  let camera_models = {}
  supported_cameras.forEach(camera => {
    camera_models[camera.brand] = camera_models[camera.brand] || []
    camera_models[camera.brand].push(camera.model_formatted)
  })
  let camera_brands = Object.keys(camera_models)

  const forms = {
    general: {
      title: '',
      description: "Use the form below to contact us and we'll e-mail you back.",
      question: 'Your question',
      fields: [
        'email',
        'message'
      ]
    },
    sales: {
      title: 'Sales',
      description: "Thanks for your interest in Arsenal! What questions will help you decide whether Arsenal is right for you?",
      question: 'Your question',
      team: 'Sales',
      fields: [
        'email',
        'message',
      ]
    },
    orders: {
      title: 'Support',
      description: "To resolve your issue efficiently, please provide the following information and we'll be in touch.",
      question: 'Detailed description of your issue',
      team: 'Order Support',
      fields: [
        'email',
        'messages',
        'confirmationNumber',
        'trackingNumber'
      ]
    },
    technical: {
      title: 'Support',
      description: "To resolve your issue efficiently, please provide the following information and we'll be in touch.",
      question: 'Detailed description of your issue',
      team: 'Tech Support',
      fields: [
        'email',
        'message',
        'cameraBrand',
        'cameraModel',
        'phoneOS',
        'arsenalVersion'
      ],
    },
    media: {
      title: '',
      description: "Thanks for your interest in Arsenal. Please provide the following information and we'll be in touch.",
      question: 'Please describe your question or inquiry.',
      team: 'Media/Influencer',
      fields: [
        'email',
        'publication',
        'deadline',
        'message'
      ]
    },
  }

  export default {
    props: ['form_type'],
    data () {
      return {
        currentStep: 1,
        submitting: false,
        email: '',
        message: '',
        cameraBrands: camera_brands,
        cameraModels: camera_models,
        cameraBrand: '',
        cameraModel: '',
        phoneOS: '',
        arsenalVersion: '',
        confirmationNumber: '',
        trackingNumber: '',
        publication: '',
        deadline: '',
        honeypot: ''
      }
    },

    validations() {
      return {
        message: { required },
        email: { required, email },
        cameraBrand: { required: requiredIf(() => this.form_type === 'technical') },
        cameraModel: { required: requiredIf(() => this.form_type === 'technical') },
        phoneOS: { required: requiredIf(() => this.form_type === 'technical') },
        confirmationNumber: { required: requiredIf(() => this.form_type === 'orders') },
        publication: { required: requiredIf(() => this.form_type === 'media') },
      }
    },

    methods: {
      formData() {
        return forms[this.form_type] || forms.general
      },

      showNextStep() {
        if (this.formValid()) {
          this.currentStep += 1
        } else {
          this.displayError()
        }
      },

      formValid() {
        this.$v.$touch()
        if (this.$v.$error) return false

        this.$v.$reset()
        return true
      },

      displayError(message = 'Some fields have errors. Please correct them and try again.') {
        this.$toast(message, { horizontalPosition: 'center', verticalPosition: 'top' })
      },

      generateMessage() {
        let message = this.message

        if (this.form_type === 'technical') {
          message += `\n\nCamera: ${this.cameraBrand} ${this.cameraModel}\nPhone Operating System: ${this.phoneOS}\nArsenal Version: ${this.arsenalVersion}`
        }

        if (this.form_type === 'orders') {
          message += `\n\nOrder Confirmation Number: ${this.confirmationNumber}\nShipment Tracking Number: ${this.trackingNumber}`
        }

        if (this.form_type === 'media') {
          message += `\n\nPublication: ${this.publication}\nDeadline: ${this.deadline}`
        }

        return message
      },

      onSubmit() {
        if (!this.formValid()) {
          this.displayError()
          return
        }

        let body = {
          email: this.email,
          message: this.generateMessage(),
          intercom_visitor_id: Intercom('getVisitorId'),
          team: forms[this.form_type].team,
          honeypot: this.honeypot
        }

        if (this.cameraBrand) {
          body['custom_attributes'] = {
            'Camera Model': `${this.cameraBrand} ${this.cameraModel}`,
            'Phone OS': this.phoneOS,
            'Version of Arsenal Remote app': this.arsenalVersion,
          }
        }

        this.submitting = true
        this.$http.post('/contacts', body).then(response => {
          this.submitting = false
          this.currentStep += 1
        }, response => {
          this.submitting = false
          this.displayError(response.body.error || 'Something went wrong. Please try again or contact support.')
        })
      },
    },
    components: { SpinnerButton }
  }
</script>
